import { LabelLangString } from "component/LabelLang/label-lang";
import { staticColorArray } from "constants/ColorConstant";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import _ from "lodash";
import { mergeWith, groupBy, flow, mapValues, isArray, values } from "lodash";
import { PeriodicTypeChecking } from "pages/data/components/functions";
import { useEffect, useState } from "react";
import { YearTypeConst } from "constants/YearTypeConstant";
import { PUBLICATIONS_PERIODIC_TYPES } from "constants/PublicationConstants";
import { Link } from "react-router-dom";

export default function LineData({ data, publication }) {
  const intl = useIntl();
  const currentLocale = useSelector(({ theme }) => theme.locale);
  const dir = useSelector(({ theme }) => theme.direction);

  // const { name, indicatorTranslations, subSubject, filters } = data;

  const [allChartData, setAllChartData] = useState([]);

  useEffect(() => {
    if (data?.filters.length > 0) {
      setAllChartData(
        data?.filters.map((el) => {
          const listOfContent = el?.data?.map((x) => {
            const monthId = x.month ?? 0;
            const quarterId = x.quarter ?? 0;
            const month = PeriodicTypeChecking({
              periodicId: publication?.periodic?.id,
              value: x.month,
              type: PUBLICATIONS_PERIODIC_TYPES.MONTH,
            });
            const quarter = PeriodicTypeChecking({
              periodicId: publication?.periodic?.id,
              value: x.quarter,
              type: PUBLICATIONS_PERIODIC_TYPES.QUARTER,
            });
            return {
              id: el.id,
              name: el.name,
              quarter:
                quarter !== "" ? intl.formatMessage({ id: quarter }) : quarter,
              month: month !== "" ? intl.formatMessage({ id: month }) : month,
              year: x.year,
              value: x.value,
              monthId: monthId,
              quarterId: quarterId,
              translations: el.filterTranslations,
            };
          });
          return listOfContent;
        })
      );
    }
  }, [data?.filters, currentLocale]);

  const sortAscHeader = (arr) => {
    var sortedArray = arr.sort(
      (a, b) => parseInt(a.sortingKey) - parseInt(b.sortingKey)
    );
    return sortedArray;
  };

  const getColHeader = () => {
    let preparingData = [];
    for (let index = 0; index < allChartData?.length; index++) {
      preparingData.push(
        allChartData[index]?.map((item) => {
          return {
            //headerName: item.year,
            headerName:
              item.month + " " + item.quarter + " " + item.year.toString(),
            sortingKey:
              item.year.toString() +
              (parseInt(item.monthId) !== 0
                ? parseInt(item.monthId) < 10
                  ? "0" + item.monthId.toString()
                  : item.monthId.toString()
                : "") +
              (parseInt(item.quarterId) !== 0 ? item.quarterId.toString() : ""),
            //row: item.value,
          };
        })
      );
    }
    const setColHeader = preparingData.flat();
    const filteredColHeader = _.uniqBy(setColHeader, (x) => x.headerName);

    const sortedFilteredColHeader = sortAscHeader(filteredColHeader);

    return sortedFilteredColHeader.length > 0 ? filteredColHeader : [];
  };

  const indicatorRendering = (indicator, translations) => {
    const subject = LabelLangString({
      id: "name", // change it
      currentLocale: currentLocale,
      defaultValue: indicator,
      translations: translations,
    });
    return subject;
  };

  const years = getColHeader();

  const [xInterval, setXInterval] = useState(0);
  useEffect(() => {
    const calculatedXInterval = Math.ceil(years.length / 9) - 1;
    setXInterval(calculatedXInterval);
  }, [years]);
  //var allYears = [...years.slice(0, 0), ...years.slice(1)];
  var allYears = [...years];
  var finalResult = data?.filters.map((item, i) => {
    return allYears.flatMap((el, index) => {
      const currentYear = el.headerName;
      const currentYearKey = el.sortingKey;

      const getRecordWithYear = item?.data?.find((att) => {
        return (
          att.year.toString() +
            ((parseInt(att.month) < 10 ? "0" + att.month : att.month) || "") +
            (att.quarter || "") ===
          currentYearKey
        );
      });

      // const currentYearNoSpace = currentYear.replaceAll(" ", "");
      const currentYearNoSpace = parseInt(currentYear.substring(0, 4));
      const yearHeaderName =
        publication?.yearType && publication?.yearType === YearTypeConst.Maly
          ? `${(
              currentYearNoSpace - 1
            ).toString()}/${currentYearNoSpace.toString()}`
          : currentYearNoSpace.toString();
      const headerName = currentYear.replace(
        `${currentYearNoSpace}`,
        `${yearHeaderName} `
      );
      if (getRecordWithYear) {
        return {
          year: headerName,
          id: getRecordWithYear.year,

          [`indicator${i}`]: item.name,
          [`value${i}`]: getRecordWithYear.value,
          [`translations${i}`]: item.filterTranslations,
        };
      } else {
        return {
          year: headerName,
          id: currentYear,

          [`indicator${i}`]: item.name,
          [`value${i}`]: null,
          [`translations${i}`]: item.filterTranslations,
        };
      }
    });
  });

  var result = flow(
    (arr) => groupBy(arr, "year"),
    (g) =>
      mapValues(g, (arr) =>
        mergeWith(...arr, (objV, srcV) => {
          if (isArray(objV)) return objV.concat(srcV);
        })
      ),
    values
  );

  var final = result(finalResult).flat();

  return (
    <>
      <h6 className="mb-5">
        {LabelLangString({
          id: "name",
          currentLocale: currentLocale,
          defaultValue: data?.name,
          translations: data?.indicatorTranslations,
        })}
      </h6>
      <ResponsiveContainer width="100%" aspect={2}>
        <LineChart
          data={final}
          style={{ textAlign: "start" }}
          margin={{ top: 15, right: 70 }}
        >
          <CartesianGrid
            strokeDasharray="1"
            verticalCoordinatesGenerator={(props) => []}
          />
          <XAxis
            axisLine={false}
            tickLine={false}
            tickAlign="start"
            textAnchor="center"
            scale="auto"
            interval={xInterval}
            dataKey={"year"}
            tick={<CustomizedXaxisTick />}
          />
          <YAxis
            axisLine={false}
            textAnchor="center"
            tick={<CustomizedYaxisTick />}
            domain={["auto", "auto"]}
          />
          <Tooltip />
          <Legend
            wrapperStyle={{
              paddingTop: "35px",
            }}
          />
          {final?.length > 0 &&
            data?.filters.map((filter, index) => {
              return (
                <Line
                  key={index}
                  type="monotone"
                  dataKey={`value${index}`}
                  // name={finalResult[0][`indicator${index}`]}
                  name={indicatorRendering(
                    final[0][`indicator${index}`],
                    final[0][`translations${index}`]
                  )}
                  stroke={staticColorArray[index]}
                  connectNulls
                />
              );
            })}
        </LineChart>
      </ResponsiveContainer>
      <div
        className="mb-3 row align-items-center gy-3 gy-md-0 gx-0 gx-md-3"
        dir={dir}
      >
        <div className="col-md-6">
          <div className="item-box-content d-flex align-items-center gap-2">
            <h6 className="fw-bold mb-0">
              <FormattedMessage id="from-publication" />:
            </h6>
            <Link
              to={`/publications/${publication?.id}`}
              style={{
                color: "var(--main-color)",
                fontWeight: "bold",
                fontSize: "14px",
              }}
            >
              {LabelLangString({
                id: "name",
                currentLocale: currentLocale,
                defaultValue: publication?.name,
                translations: publication?.publicationTranslations,
              })}
            </Link>
          </div>
        </div>
        <div className="col-md-6">
          <div className="item-box-content d-flex align-items-center gap-2">
            <h6 className="fw-bold mb-0">
              <FormattedMessage id="browse-topic" />:
            </h6>
            <span
              style={{
                color: "var(--main-color)",
                fontWeight: "bold",
                fontSize: "14px",
              }}
            >
              {LabelLangString({
                id: "name",
                currentLocale: currentLocale,
                defaultValue: data?.subSubject?.name,
                translations: data?.subSubject?.translations,
              })}
            </span>
          </div>
        </div>
      </div>
    </>
  );
}

const CustomizedXaxisTick = (props) => {
  const { x, y, payload } = props;
  const { value } = payload;
  var splittedValueArr = value.split(" ");
  splittedValueArr = splittedValueArr.filter((n) => n);
  return (
    // <g transform={`translate(${x},${y})`}>
    <g transform={`translate(${x},${y})`}>
      <text x="0" y="0" dy="0" fill="#666" transform="rotate(0)">
        {splittedValueArr.map((obj, index) => {
          return (
            <tspan key={index} x="-15" dy={(15 + index).toString()}>
              {obj}
            </tspan>
          );
        })}
      </text>
    </g>
  );
};

const CustomizedYaxisTick = (props) => {
  const { x, y, payload } = props;
  const { value } = payload;
  return (
    <g transform={`translate(${x},${y})`}>
      <text x="-30" y="5" dy="0" fill="#666" transform="rotate(0)">
        {value}
      </text>
    </g>
  );
};
